import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice, formatApiError } from "../../lib/api";

const EMPTY = {
    name: "",
    description: "",
    category: "clothing",
    price: 0,
    compare_at_price: null,
    stock: 0,
    images: [],
    sizes: [],
    colors: [],
    featured: false,
    tags: [],
};

export default function AdminProducts() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null = closed, {} = new, {id...} = edit

    const fetchAll = () => {
        setLoading(true);
        api.get("/products?limit=200")
            .then(({ data }) => setProducts(data.products))
            .finally(() => setLoading(false));
    };

    useEffect(fetchAll, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this product?")) return;
        try {
            await api.delete(`/products/${id}`);
            toast.success("Deleted");
            fetchAll();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const onSaved = () => {
        setEditing(null);
        fetchAll();
    };

    return (
        <div data-testid="admin-products">
            <div className="flex items-end justify-between">
                <div>
                    <p className="text-[11px] uppercase tracking-[0.4em] text-gold">Atelier</p>
                    <h1 className="mt-2 font-display text-4xl text-white">Products</h1>
                </div>
                <button
                    onClick={() => setEditing({ ...EMPTY })}
                    data-testid="admin-add-product"
                    className="inline-flex items-center gap-2 bg-gold px-5 py-3 text-[11px] uppercase tracking-[0.25em] text-obsidian hover:bg-gold-light"
                >
                    <Plus className="h-3 w-3" /> Add product
                </button>
            </div>

            <div className="mt-8 border border-border-dim bg-surface">
                <div className="hidden grid-cols-12 gap-4 border-b border-border-dim px-6 py-4 text-[10px] uppercase tracking-[0.25em] text-white/40 lg:grid">
                    <div className="col-span-5">Product</div>
                    <div className="col-span-2">Category</div>
                    <div className="col-span-2 text-right">Price</div>
                    <div className="col-span-1 text-right">Stock</div>
                    <div className="col-span-2 text-right">Actions</div>
                </div>
                {loading ? (
                    <div className="p-10 text-white/40">Loading…</div>
                ) : (
                    products.map((p) => (
                        <div key={p.id} className="border-b border-border-dim p-4 last:border-b-0 lg:grid lg:grid-cols-12 lg:items-center lg:gap-4 lg:px-6" data-testid={`admin-product-${p.id}`}>
                            {/* Mobile card */}
                            <div className="flex gap-4 lg:col-span-5">
                                <img src={p.images?.[0]} alt="" className="h-16 w-14 flex-shrink-0 object-cover" />
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm text-white">{p.name}</p>
                                    <p className="mt-1 text-[10px] uppercase tracking-widest text-white/40">
                                        {p.category} {p.featured ? "· Featured" : ""}
                                    </p>
                                    <div className="mt-2 flex items-center gap-3 text-xs lg:hidden">
                                        <span className="text-gold">{formatPrice(p.price)}</span>
                                        <span className={p.stock < 10 ? "text-heart" : "text-white/60"}>
                                            Stock: {p.stock}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex gap-2 lg:hidden">
                                    <button
                                        onClick={() => setEditing(p)}
                                        data-testid={`edit-${p.id}`}
                                        className="flex h-9 w-9 items-center justify-center border border-border-dim text-white/70 hover:border-gold hover:text-gold"
                                    >
                                        <Edit2 className="h-3 w-3" />
                                    </button>
                                    <button
                                        onClick={() => remove(p.id)}
                                        data-testid={`delete-${p.id}`}
                                        className="flex h-9 w-9 items-center justify-center border border-border-dim text-white/70 hover:border-heart hover:text-heart"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </button>
                                </div>
                            </div>
                            <div className="hidden text-xs uppercase tracking-widest text-white/60 lg:col-span-2 lg:block">{p.category}</div>
                            <div className="hidden text-right text-sm text-gold lg:col-span-2 lg:block">{formatPrice(p.price)}</div>
                            <div className={`hidden text-right text-sm lg:col-span-1 lg:block ${p.stock < 10 ? "text-heart" : "text-white"}`}>{p.stock}</div>
                            <div className="hidden justify-end gap-2 lg:col-span-2 lg:flex">
                                <button
                                    onClick={() => setEditing(p)}
                                    className="flex h-9 w-9 items-center justify-center border border-border-dim text-white/70 hover:border-gold hover:text-gold"
                                >
                                    <Edit2 className="h-3 w-3" />
                                </button>
                                <button
                                    onClick={() => remove(p.id)}
                                    className="flex h-9 w-9 items-center justify-center border border-border-dim text-white/70 hover:border-heart hover:text-heart"
                                >
                                    <Trash2 className="h-3 w-3" />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {editing && <ProductFormModal initial={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}
        </div>
    );
}

function ProductFormModal({ initial, onClose, onSaved }) {
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const isEdit = !!initial?.id;

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                ...form,
                price: parseFloat(form.price) || 0,
                stock: parseInt(form.stock) || 0,
                compare_at_price: form.compare_at_price ? parseFloat(form.compare_at_price) : null,
                images: typeof form.images === "string" ? form.images.split("\n").map((s) => s.trim()).filter(Boolean) : form.images,
                sizes: typeof form.sizes === "string" ? form.sizes.split(",").map((s) => s.trim()).filter(Boolean) : form.sizes,
                colors: typeof form.colors === "string" ? form.colors.split(",").map((s) => s.trim()).filter(Boolean) : form.colors,
                tags: typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : form.tags,
            };
            if (isEdit) {
                await api.put(`/products/${initial.id}`, payload);
            } else {
                await api.post("/products", payload);
            }
            toast.success(isEdit ? "Updated" : "Created");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="product-modal">
            <div onClick={(e) => e.stopPropagation()} className="max-h-[90vh] w-full max-w-3xl overflow-y-auto border border-border-dim bg-surface p-8">
                <div className="flex items-start justify-between">
                    <h2 className="font-display text-3xl text-white">{isEdit ? "Edit product" : "New product"}</h2>
                    <button onClick={onClose} className="text-white/60 hover:text-heart">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field label="Name" span={2}>
                        <input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="form-name" className={inp} />
                    </Field>
                    <Field label="Category">
                        <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="form-category" className={inp}>
                            <option value="clothing">Clothing</option>
                            <option value="home">Home</option>
                            <option value="office">Office</option>
                        </select>
                    </Field>
                    <Field label="Price (USD)">
                        <input type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="form-price" className={inp} />
                    </Field>
                    <Field label="Compare-at price">
                        <input type="number" step="0.01" value={form.compare_at_price || ""} onChange={(e) => set("compare_at_price", e.target.value)} className={inp} />
                    </Field>
                    <Field label="Stock">
                        <input type="number" value={form.stock} onChange={(e) => set("stock", e.target.value)} data-testid="form-stock" className={inp} />
                    </Field>
                    <Field label="Description" span={2}>
                        <textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className={inp} />
                    </Field>
                    <Field label="Images (one URL per line)" span={2}>
                        <textarea
                            rows={3}
                            value={Array.isArray(form.images) ? form.images.join("\n") : form.images}
                            onChange={(e) => set("images", e.target.value)}
                            data-testid="form-images"
                            className={inp}
                        />
                    </Field>
                    <Field label="Sizes (comma sep)">
                        <input
                            value={Array.isArray(form.sizes) ? form.sizes.join(", ") : form.sizes}
                            onChange={(e) => set("sizes", e.target.value)}
                            className={inp}
                        />
                    </Field>
                    <Field label="Colors (comma sep)">
                        <input
                            value={Array.isArray(form.colors) ? form.colors.join(", ") : form.colors}
                            onChange={(e) => set("colors", e.target.value)}
                            className={inp}
                        />
                    </Field>
                    <Field label="Tags (comma sep)" span={2}>
                        <input
                            value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags}
                            onChange={(e) => set("tags", e.target.value)}
                            className={inp}
                        />
                    </Field>
                    <label className="flex items-center gap-3 md:col-span-2">
                        <input
                            type="checkbox"
                            checked={!!form.featured}
                            onChange={(e) => set("featured", e.target.checked)}
                            data-testid="form-featured"
                            className="h-4 w-4"
                        />
                        <span className="text-sm text-white">Featured on homepage</span>
                    </label>
                </div>
                <div className="mt-8 flex justify-end gap-3">
                    <button onClick={onClose} className="border border-border-dim px-6 py-3 text-[11px] uppercase tracking-widest text-white/70 hover:text-white">
                        Cancel
                    </button>
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="form-save"
                        className="bg-gold px-6 py-3 text-[11px] uppercase tracking-widest text-obsidian hover:bg-gold-light disabled:opacity-50"
                    >
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

const inp = "w-full border border-border-dim bg-transparent px-3 py-2 text-sm text-white outline-none focus:border-gold";

function Field({ label, children, span = 1 }) {
    return (
        <div className={span === 2 ? "md:col-span-2" : ""}>
            <label className="text-[10px] uppercase tracking-[0.25em] text-white/50">{label}</label>
            <div className="mt-2">{children}</div>
        </div>
    );
}
